import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Button, SanitizedHtml } from '@/components/shared/ui';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import { useFaqList } from '@/hooks/useFaq';
import { groupFaqsByCategory } from '@/lib/faqGrouping';
import type { FaqListItemDto } from '@/types/api/faq';

interface FaqItemProps {
  faq: FaqListItemDto;
  isOpen: boolean;
  onToggle: (faqId: number) => void;
}

const FaqItem = memo(function FaqItem({ faq, isOpen, onToggle }: FaqItemProps): React.ReactElement {
  const answerPanelId = `faq-answer-${faq.id}`;

  const handleToggle = useCallback(() => {
    onToggle(faq.id);
  }, [onToggle, faq.id]);

  return (
    <li className="border-b border-gray-200 last:border-b-0">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-controls={answerPanelId}
        className="flex w-full items-center justify-between gap-4 px-2 py-4 text-start text-base font-medium text-gray-900 hover:text-brand-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-600"
      >
        <span className="flex min-w-0 flex-1 items-baseline gap-3">
          <span className="w-4 shrink-0 font-bold text-brand-600" aria-hidden="true">
            Q
          </span>
          <span className="min-w-0 break-words">{faq.title}</span>
        </span>
        <ChevronDown
          size={20}
          className={`shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className="mb-4 flex w-full items-start gap-3 px-2">
          <span className="w-4 shrink-0 text-base font-bold leading-6 text-gray-400" aria-hidden="true">
            A
          </span>
          <SanitizedHtml
            id={answerPanelId}
            className="min-w-0 flex-1 text-gray-700 prose prose-gray max-w-none break-words"
            html={faq.content}
          />
        </div>
      )}
    </li>
  );
});

function FaqSkeleton(): React.ReactElement {
  return (
    <div className="space-y-3 animate-pulse" aria-hidden="true">
      <div className="h-5 w-24 rounded bg-gray-200" />
      <div className="h-12 rounded bg-gray-100" />
      <div className="h-12 rounded bg-gray-100" />
      <div className="h-12 rounded bg-gray-100" />
    </div>
  );
}

/**
 * 고객문의 페이지 자주 묻는 질문 영역.
 * 카테고리는 STUDENT → MASTER 순으로 고정, 항목 클릭 시 본문이 열린다.
 */
export default function FaqSection(): React.ReactElement {
  const { t } = useTranslation('menu');
  const apiLanguage = useApiLanguage();
  const { data: faqListResponse, isLoading, isError, refetch } = useFaqList(apiLanguage);
  const [openFaqIds, setOpenFaqIds] = useState<ReadonlySet<number>>(() => new Set());

  const categoryGroups = useMemo(
    () => groupFaqsByCategory(faqListResponse?.faqs ?? []),
    [faqListResponse],
  );

  const toggleFaq = useCallback((faqId: number) => {
    setOpenFaqIds((prevOpenIds) => {
      const nextOpenIds = new Set(prevOpenIds);
      if (nextOpenIds.has(faqId)) {
        nextOpenIds.delete(faqId);
      } else {
        nextOpenIds.add(faqId);
      }
      return nextOpenIds;
    });
  }, []);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const renderBody = (): React.ReactNode => {
    if (isLoading) {
      return <FaqSkeleton />;
    }
    if (isError) {
      return (
        <div className="py-8 text-center">
          <p className="mb-4 text-gray-600">{t('contactPage.faq.loadError')}</p>
          <Button type="button" variant="lightdark" size="md" onClick={handleRetry}>
            {t('contactPage.faq.retry')}
          </Button>
        </div>
      );
    }
    if (categoryGroups.length === 0) {
      return <p className="py-8 text-center text-gray-500">{t('contactPage.faq.empty')}</p>;
    }
    return (
      <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-2">
        {categoryGroups.map((group) => (
          <div key={group.category}>
            <h3 className="mb-2 border-s-4 border-brand-600 ps-3 text-lg font-semibold leading-tight text-gray-900">
              {t(`contactPage.faq.categories.${group.category}`)}
            </h3>
            <ul className="border-t border-gray-200">
              {group.faqs.map((faq) => (
                <FaqItem
                  key={faq.id}
                  faq={faq}
                  isOpen={openFaqIds.has(faq.id)}
                  onToggle={toggleFaq}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  return (
    <section className="mt-8 rounded-lg border border-gray-200 bg-white p-6" aria-labelledby="faq-section-title">
      <h2 id="faq-section-title" className="mb-8 text-lg font-semibold text-gray-900">
        {t('contactPage.faq.title')}
      </h2>
      {renderBody()}
    </section>
  );
}
